/**
 * Dead Card Parser
 *
 * Parses user input for dead/removed cards.
 * Format: Rank + Suit (e.g., "Ac", "7h", "Td")
 *
 * Ranks: A, K, Q, J, T, 9, 8, 7, 6, 5, 4, 3, 2
 * Suits: c (clubs), d (diamonds), h (hearts), s (spades)
 */

const VALID_RANKS = new Set(['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2']);
const VALID_SUITS = new Set(['c', 'd', 'h', 's']);

// Suit symbol to letter mapping
const SUIT_SYMBOLS: Record<string, string> = {
  '♣': 'c',
  '♦': 'd',
  '♥': 'h',
  '♠': 's',
  '♧': 'c', // alternate club
  '♢': 'd', // alternate diamond
  '♡': 'h', // alternate heart
  '♤': 's', // alternate spade
};

/**
 * Normalize a card string to canonical format (e.g., "Ac", "7h")
 * Returns null if invalid
 */
export function normalizeCard(card: string): string | null {
  const trimmed = card.trim();
  if (trimmed.length < 2) return null;

  // Extract rank (first character, uppercase)
  const rank = trimmed[0].toUpperCase();
  if (!VALID_RANKS.has(rank)) return null;

  // Extract suit (remaining characters)
  let suitPart = trimmed.slice(1).toLowerCase();

  // Check for suit symbols
  if (SUIT_SYMBOLS[suitPart]) {
    suitPart = SUIT_SYMBOLS[suitPart];
  }

  // Validate suit
  if (suitPart.length !== 1 || !VALID_SUITS.has(suitPart)) return null;

  return `${rank}${suitPart}`;
}

/**
 * Parse a comma-separated list of dead cards
 * Returns an array of valid, normalized card strings (no duplicates)
 */
export function parseDeadCards(input: string): string[] {
  if (!input.trim()) return [];

  const cards = input
    .split(/[,\s]+/) // Split on comma or whitespace
    .map(normalizeCard)
    .filter((card): card is string => card !== null);

  // Remove duplicates
  return [...new Set(cards)];
}

/**
 * Get all 52 cards in the deck
 */
export function getAllCards(): string[] {
  const cards: string[] = [];
  for (const rank of VALID_RANKS) {
    for (const suit of VALID_SUITS) {
      cards.push(`${rank}${suit}`);
    }
  }
  return cards;
}

/**
 * Validate if a string is a valid card
 */
export function isValidCard(card: string): boolean {
  return normalizeCard(card) !== null;
}

/**
 * Format cards for display (e.g., "Ac, 7h, 2d")
 */
export function formatDeadCards(cards: string[]): string {
  return cards.join(', ');
}

/**
 * Get the rank from a card (e.g., "Ac" -> "A")
 */
export function getCardRank(card: string): string {
  return card[0];
}

/**
 * Get the suit from a card (e.g., "Ac" -> "c")
 */
export function getCardSuit(card: string): string {
  return card[1];
}
